import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaEnvelope, FaWeight, FaRulerVertical, FaPhone, FaBirthdayCake, FaMapMarkerAlt } from 'react-icons/fa';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 300px 1fr;
  }
`;

const ProfileImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProfileImage = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid ${({ theme }) => theme.colors.primary};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    font-size: 5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundAlt};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  padding: 0.5rem;
  background-color: #f0fff4;
  border-radius: 4px;
  text-align: center;
`;

const HealthDataSection = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CustomerProfile = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    // Health data
    height: '',
    weight: '',
    gender: '',
    activityLevel: '',
    dietaryRestrictions: '',
    healthGoals: ''
  });
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Fetch user profile data
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          
          // Fetch health data
          const healthRef = doc(db, 'users', currentUser.uid, 'health', 'profile');
          const healthDoc = await getDoc(healthRef);
          
          if (healthDoc.exists()) {
            setHealthData(healthDoc.data());
            
            // Update form data with all available info
            setFormData({
              displayName: userData.displayName || '',
              email: userData.email || '',
              phone: userData.phone || '',
              dateOfBirth: userData.dateOfBirth || '',
              address: userData.address || '',
              city: userData.city || '',
              state: userData.state || '',
              zipCode: userData.zipCode || '',
              country: userData.country || '',
              // Health data
              height: healthDoc.data().height || '',
              weight: healthDoc.data().weight || '',
              gender: healthDoc.data().gender || '',
              activityLevel: healthDoc.data().activityLevel || '',
              dietaryRestrictions: healthDoc.data().dietaryRestrictions || '',
              healthGoals: healthDoc.data().healthGoals || ''
            });
          } else {
            // Just update with user data
            setFormData({
              ...formData,
              displayName: userData.displayName || '',
              email: userData.email || '',
              phone: userData.phone || '',
              dateOfBirth: userData.dateOfBirth || '',
              address: userData.address || '',
              city: userData.city || '',
              state: userData.state || '',
              zipCode: userData.zipCode || '',
              country: userData.country || ''
            });
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load your profile data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Update profile in Firebase Auth and Firestore
      const profileData = {
        displayName: formData.displayName,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country
      };
      
      await updateUserProfile(currentUser, profileData);
      
      // Update health data separately
      const healthData = {
        height: formData.height,
        weight: formData.weight,
        gender: formData.gender,
        activityLevel: formData.activityLevel,
        dietaryRestrictions: formData.dietaryRestrictions,
        healthGoals: formData.healthGoals,
        updatedAt: new Date()
      };
      
      const healthRef = doc(db, 'users', currentUser.uid, 'health', 'profile');
      await setDoc(healthRef, healthData, { merge: true });
      
      setSuccess('Profile updated successfully!');
      
      // Update local userData state
      setUserData(prev => ({
        ...prev,
        ...profileData
      }));
      
      // Update local healthData state
      setHealthData(prev => ({
        ...prev,
        ...healthData
      }));
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !userData) {
    return (
      <PageContainer>
        <Title>My Profile</Title>
        <Subtitle>Loading your profile data...</Subtitle>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <Title>My Profile</Title>
      <Subtitle>View and update your personal information</Subtitle>
      
      <ProfileGrid>
        <ProfileImageSection>
          <ProfileImage>
            {currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt="Profile" />
            ) : (
              <FaUser />
            )}
          </ProfileImage>
          <Button variant="outline">Change Photo</Button>
        </ProfileImageSection>
        
        <div>
          <Card>
            <Card.Header>Personal Information</Card.Header>
            <Card.Content>
              <form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="displayName">Full Name</Label>
                    <Input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </FormRow>
                
                <SectionTitle>Address Information</SectionTitle>
                
                <FormGroup>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="city">City</Label>
                    <Input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="zipCode">Zip/Postal Code</Label>
                    <Input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </FormRow>
                
                <HealthDataSection>
                  <SectionTitle>Health Information</SectionTitle>
                  
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="height">Height (cm)</Label>
                      <Input
                        type="number"
                        id="height"
                        name="height"
                        value={formData.height}
                        onChange={handleChange}
                        min="100"
                        max="250"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="weight">Weight (kg)</Label>
                      <Input
                        type="number"
                        id="weight"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        min="30"
                        max="300"
                        step="0.1"
                      />
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="gender">Gender</Label>
                      <Select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </Select>
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="activityLevel">Activity Level</Label>
                      <Select
                        id="activityLevel"
                        name="activityLevel"
                        value={formData.activityLevel}
                        onChange={handleChange}
                      >
                        <option value="">Select activity level</option>
                        <option value="sedentary">Sedentary (little or no exercise)</option>
                        <option value="light">Lightly active (light exercise/sports 1-3 days/week)</option>
                        <option value="moderate">Moderately active (moderate exercise/sports 3-5 days/week)</option>
                        <option value="very">Very active (hard exercise/sports 6-7 days a week)</option>
                        <option value="extra">Extra active (very hard exercise & physical job)</option>
                      </Select>
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="dietaryRestrictions">Dietary Restrictions</Label>
                      <Select
                        id="dietaryRestrictions"
                        name="dietaryRestrictions"
                        value={formData.dietaryRestrictions}
                        onChange={handleChange}
                      >
                        <option value="">None</option>
                        <option value="vegetarian">Vegetarian</option>
                        <option value="vegan">Vegan</option>
                        <option value="gluten-free">Gluten-free</option>
                        <option value="dairy-free">Dairy-free</option>
                        <option value="keto">Keto</option>
                        <option value="paleo">Paleo</option>
                      </Select>
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="healthGoals">Health Goals</Label>
                      <Select
                        id="healthGoals"
                        name="healthGoals"
                        value={formData.healthGoals}
                        onChange={handleChange}
                      >
                        <option value="">Select your primary goal</option>
                        <option value="weight-loss">Weight loss</option>
                        <option value="muscle-gain">Muscle gain</option>
                        <option value="maintenance">Weight maintenance</option>
                        <option value="health-improvement">General health improvement</option>
                        <option value="athletic-performance">Athletic performance</option>
                      </Select>
                    </FormGroup>
                  </FormRow>
                </HealthDataSection>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                
                <Button type="submit" disabled={loading} style={{ marginTop: '1.5rem' }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </Card.Content>
          </Card>
        </div>
      </ProfileGrid>
    </PageContainer>
  );
};

export default CustomerProfile; 